// Shared look for chat cards, labels, and buttons.
// Keep complete Tailwind classes here so every tone is included in the build.
//
// The chat is neutral (gray/slate) with the brand lime as its only accent. Surface, not hue,
// tells the kinds of card apart on the white chat: what the builder acts on is a slate-tinted,
// bordered, lifted card whose fields and options are white; what the copilot reports is a flat,
// deeper gray panel that recedes behind the conversation. Colour is kept for meaning only
// (needs a decision, succeeded, failed) and sits in the small icon tile.

const raised = 'border-slate-200/80 bg-slate-100 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_6px_20px_-12px_rgba(15,23,42,0.2)] dark:shadow-none';
const flat = 'border-gray-200 bg-gray-50';

const neutralIcon = 'bg-white text-gray-600 ring-gray-200';
const neutralBadge = 'border-gray-200 bg-white text-gray-700';
const neutralAccent = 'text-gray-500';

const brandIcon = 'bg-gray-900 text-brand-lime ring-gray-900 dark:bg-brand-lime dark:ring-brand-lime';

const tones = {
    // Something to fill in or pick: forms, onboarding questions, uploads. Its tile is the brand's black and lime.
    knowledge: { surface: raised, icon: brandIcon, badge: neutralBadge, accent: neutralAccent },
    // Progress and results the copilot reports: plan, tests, notes.
    planning: { surface: flat, icon: neutralIcon, badge: neutralBadge, accent: neutralAccent },
    // Waiting on the builder's decision.
    decision: {
        surface: raised,
        icon: 'bg-amber-50 text-amber-600 ring-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20',
        badge: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200',
        accent: 'text-amber-700 dark:text-amber-300',
    },
    success: {
        surface: flat,
        icon: 'bg-lime-50 text-lime-700 ring-lime-200 dark:bg-brand-lime/10 dark:text-brand-lime dark:ring-brand-lime/20',
        badge: 'border-lime-200 bg-lime-50 text-lime-800 dark:border-brand-lime/20 dark:bg-brand-lime/10 dark:text-brand-lime',
        accent: 'text-lime-700 dark:text-brand-lime',
    },
    danger: {
        surface: 'border-red-200/70 bg-red-50/50 dark:border-red-900/50 dark:bg-red-950/20',
        icon: 'bg-red-50 text-red-600 ring-red-200 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20',
        badge: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300',
        accent: 'text-red-600 dark:text-red-400',
    },
    brand: {
        surface: raised,
        icon: brandIcon,
        badge: neutralBadge,
        accent: 'text-gray-900 dark:text-brand-lime',
    },
};

export const chatTone = name => tones[name] || tones.knowledge;
export const chatCard = name => `rounded-2xl border p-4 text-gray-900 ${chatTone(name).surface}`;
/** The small square that holds a card's icon; carries the card's meaning through colour. */
export const chatIconTile = name => `flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 ${chatTone(name).icon}`;
/** A neutral chip: sources, changed resources. */
export const chatLabel = () => 'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700';
export const chatEyebrow = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500';
export const chatField = 'min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[0.875rem] text-gray-900 placeholder:text-gray-400 transition-colors focus:border-lime-600 focus:outline-none focus:ring-2 focus:ring-lime-500/20 disabled:opacity-60 dark:focus:border-brand-lime';
export const chatFocus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-600 focus-visible:ring-offset-2 dark:focus-visible:ring-brand-lime';
export const chatPrimary = `inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-900 px-3.5 py-2 text-sm font-semibold text-brand-lime transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-lime dark:hover:bg-lime-300 ${chatFocus}`;
export const chatSecondary = `inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 ${chatFocus}`;
export const chatGhost = `inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[0.8rem] font-medium text-gray-600 transition-colors hover:bg-slate-200 hover:text-gray-900 disabled:opacity-50 ${chatFocus}`;
